// ConfirmDialog — potvrdzovací modál pre nevratné operácie
// Použitie:
//   const confirm = useConfirm()
//   const ok = await confirm({ title, message, danger: true, confirmLabel: 'Vymazať' })
// S dôvodom (cena_v2, 30. 9. 2026) — nahrádza window.prompt, ktorý prehliadač po „Prevent this page from creating
// additional dialogues" navždy umlčí a tlačidlo potom nerobí nič:
//   const v = await confirm({ title, message, dovod: { label: 'Dôvod opravy', hint, placeholder, povinne: true } })
//   v === false → zrušené · inak { dovod: '…' }
import React, { createContext, useContext, useEffect, useState } from 'react'
import { COLORS, TYPO, SPACE, RADIUS } from '../theme.js'

const Ctx = createContext(null)

export function ConfirmProvider({ children }) {
  const [req, setReq] = useState(null)  // { title, message, danger, confirmLabel, dovod, resolve }
  const [text, setText] = useState('')
  const [chyba, setChyba] = useState('')
  function ask(opts) {
    return new Promise((resolve) => {
      setText(String((opts.dovod && opts.dovod.hodnota) || ''))
      setChyba('')
      setReq({
        title: opts.title || 'Potvrdiť operáciu',
        message: opts.message || 'Naozaj pokračovať?',
        danger: !!opts.danger,
        confirmLabel: opts.confirmLabel || 'Potvrdiť',
        cancelLabel: opts.cancelLabel || 'Zrušiť',
        dovod: opts.dovod || null,
        resolve,
      })
    })
  }
  function close(result) {
    if (req?.resolve) req.resolve(result)
    setReq(null); setText(''); setChyba('')
  }
  // Tlačidlo NIKDY nie je ticho sivé (30. 9. 2026): keď dôvod chýba, klik to povie nahlas.
  // Sivé tlačidlo na klik nezavolá nič a používateľ nevie, čo mu chýba.
  function potvrd() {
    if (req && req.dovod && req.dovod.povinne !== false && !text.trim()) { setChyba(req.dovod.chybaText || 'Bez dôvodu to nejde ďalej — napíš, prečo sa to mení.'); return }
    close(req && req.dovod ? { dovod: text.trim() } : true)
  }
  useEffect(() => {
    if (!req) return undefined
    const f = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); close(false) }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); potvrd() }
    }
    window.addEventListener('keydown', f)
    return () => window.removeEventListener('keydown', f)
  })
  return (
    <Ctx.Provider value={ask}>
      {children}
      {req && (
        <div onClick={() => close(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: COLORS.bg, borderRadius: RADIUS.lg, padding: SPACE.lg,
            width: 480, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: SPACE.md,
            borderTop: `4px solid ${req.danger ? COLORS.remove : COLORS.brand}`,
          }}>
            <h3 style={{ margin: 0, color: req.danger ? COLORS.remove : COLORS.text }}>
              {req.title}
            </h3>
            <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.text, lineHeight: 1.5, whiteSpace: 'pre-line' }}>
              {req.message}
            </div>
            {req.dovod && (
              <div>
                <label style={{ display: 'block', fontSize: TYPO.sizes.xs, fontWeight: 700, marginBottom: 3 }}>
                  {req.dovod.label || 'Dôvod'}{req.dovod.povinne !== false ? <b style={{ color: COLORS.remove }}> *</b> : null}
                </label>
                <textarea value={text} autoFocus rows={req.dovod.riadky || 2} placeholder={req.dovod.placeholder || ''}
                  onChange={e => { setText(e.target.value); if (chyba) setChyba('') }}
                  style={{
                    width: '100%', padding: '7px 10px', fontSize: TYPO.sizes.base, fontFamily: TYPO.family,
                    border: `1px solid ${chyba ? COLORS.remove : COLORS.line}`, borderRadius: RADIUS.md,
                    background: COLORS.bg, boxSizing: 'border-box', resize: 'vertical',
                  }} />
                {(chyba || req.dovod.hint) && <div style={{ fontSize: TYPO.sizes.xs, color: chyba ? COLORS.remove : COLORS.muted, marginTop: 3 }}>{chyba || req.dovod.hint}</div>}
              </div>
            )}
            <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
              <button onClick={() => close(false)} autoFocus={!req.dovod} style={{
                padding: '8px 16px', background: 'transparent', color: COLORS.text,
                border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm,
                cursor: 'pointer', fontFamily: 'inherit', fontSize: TYPO.sizes.sm,
              }}>{req.cancelLabel}</button>
              <button onClick={potvrd} style={{
                padding: '8px 16px',
                background: req.danger ? COLORS.remove : COLORS.add, color: '#fff',
                border: 'none', borderRadius: RADIUS.sm,
                cursor: 'pointer', fontFamily: 'inherit', fontSize: TYPO.sizes.sm,
                fontWeight: TYPO.weights.bold,
              }}>{req.confirmLabel}</button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(Ctx)
  if (!ctx) {
    return async (opts) => (opts && opts.dovod ? false : window.confirm(opts.message || 'Naozaj?'))
  }
  return ctx
}
